import {NgModule} from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LoginFormComponent } from './login/feature/loginForm/loginForm.component';
import { LoginTermsComponent } from './login/feature/loginTerms/loginTerms.component';
import { LoginResearchComponent } from './login/feature/loginResearch/loginResearch.component';
import { StudentDashboardComponent } from './dashboard/feature/studentDashboard/studentDashboard.component';
import { LoginRootComponent } from './login/feature/loginRoot.component';
import { UserManagementListComponent } from './admin/feature/userManagement/userManagementList/userManagementList.component';
import { UserManagementMenuComponent } from "./admin/feature/userManagement/userManagementMenu.component";
import { AgreementManagementList } from './admin/feature/agreement/agreementManagementList/agreementManagementList.component';
import { authGuard } from './login/utils/auth.guard';
import { requiredAgreementsGuard } from './admin/utils/required-agreements.guard';
import { optionalAgreementsGuard } from './admin/utils/optional-agreements.guard';
import { permissionsGuard } from './login/utils/permissions.guard';
import {DashboardWrapperComponent} from "./dashboard/ui/dashboard-wrapper/dashboard-wrapper.component";
import {
  CreateEditAgreement
} from "./admin/feature/agreement/createEditAgreement/createEditAgreement.component";
import {UserProfileComponent} from "./dashboard/userProfile/userProfile.component";
import {
  AgreementResponseList
} from "./admin/feature/agreement/agreementResponseList/agreementResponseList.component";
import {AgreementPreview} from "./admin/feature/agreement/agreementPreview/agreementPreview.component";
import { InstructorDashboardComponent } from './dashboard/feature/instructorDashboard/instructorDashboard.component';
import { LoginResolver } from './login/utils/login.resolver';
import {UserRolesListComponent} from "./admin/feature/userManagement/userRolesList/userRolesList.component";
import {UserRoleAddEditComponent} from "./admin/feature/userManagement/userRoleAddEdit/userRoleAddEdit.component";
import {AddNewUserComponent} from "./admin/feature/userManagement/addNewUser/addNewUser.component";
import {ImportUsersComponent} from "./admin/feature/userManagement/importUsers/importUsers.component";
import {RoleAssigneesComponent} from "./admin/feature/userManagement/roleAssignees/roleAssignees.component";
import {AutoImportUsersComponent} from "./admin/feature/userManagement/autoImportUsers/autoImportUsers.component";
import {AutoImportLogComponent} from "./admin/feature/userManagement/autoImportLog/autoImportLog.component";
import {SubjectListComponent} from "./admin/feature/contentViewer/subjectList/subjectList.component";
import {LearningObjectiveList} from "./admin/feature/contentViewer/learningObjective/learningObjective.component";
import {CCLTreeComponent} from "./admin/feature/contentViewer/cclTree/cclTree.component";
import {ProblemTemplateList} from "./admin/feature/contentViewer/problemTemplateList/problemTemplateList.component";
import {
  ProblemTemplatePreview
} from "./admin/feature/contentViewer/problemTemplatePreview/problemTemplatePreview.component";
import {CreateComponent} from "./exam/feature/create/create.component";
import {pendingChangesGuard} from "./exam/utils/pending-changes.guard";
import {ListExamComponent} from "./exam/feature/list/list.component";
import {ExamTakeComponent} from "./exam/feature/exam-take/exam-take.component";
import { AutoImportUsersConfigListComponent } from './admin/feature/userManagement/autoImportUsersConfigList/AutoImportUsersConfigListComponent';
import {ExamGradersComponent} from "./exam/feature/exam-graders/exam-graders.component";
import {ExamSubmissionsComponent} from "./exam/feature/exam-submissions/exam-submissions.component";
import {ExamSubmissionReviewComponent} from "./exam/feature/exam-submission-review/exam-submission-review.component";
import {ExamAllInstancesComponent} from "./exam/feature/exam-all-instances/exam-all-instances.component";
import {
  ExamInstanceCommentsComponent
} from "./exam/feature/exam-all-instances/exam-instance-comments/exam-instance-comments.component";
import {ExamExportConfigComponent} from "./exam/feature/exam-export/exam-export-config/exam-export-config.component";
import {ExamExportListComponent} from "./exam/feature/exam-export/exam-export-list/exam-export-list.component";
import {SystemConfigComponent} from "./admin/feature/system-management/system-config/system-config.component";
import {SystemManagementMenuComponent} from "./admin/feature/system-management/system-management-menu.component";
import {
  OrganizationListComponent
} from "./admin/feature/system-management/organization-list/organization-list.component";
import {UnitListComponent} from "./admin/feature/system-management/units-list/unit-list.component";
import {CourseListComponent} from "./admin/feature/system-management/courses-list/course-list.component";
import {SystemSubjectListComponent} from "./admin/feature/system-management/subjects-list/subject-list.component";
import {SystemBrandingComponent} from "./admin/feature/system-management/system-branding/system-branding.component";
import {
  AnnouncementConfigComponent
} from "./admin/feature/system-management/announcement-config/announcement-config.component";
import {
  AnnouncementListComponent
} from "./admin/feature/system-management/announcement-list/announcement-list.component";
import {SystemListHubComponent} from "./admin/feature/system-management/system-list-hub/system-list-hub.component";
import {ForbiddenErrorComponent} from "./shared/ui/forbiddenError/forbidden-error.component";
import {SystemLogListComponent} from "./admin/feature/system-management/system-log-list/system-log-list.component";
import {GuideListComponent} from "./admin/feature/guides/guide-list.component";
import {ServerErrorComponent} from "./login/feature/server-error/server-error.component";
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import {
  StudentClassroomDashboardComponent
} from "./dashboard/feature/studentClassroomDashboard/studentClassroomDashboard.component";
import {
  StudentClassroomAssessmentsComponent
} from "./student-classroom/assessments/student-classroom-assessments.component";
import {StudentGradebookComponent} from "./student-classroom/gradebook/student-gradebook.component";
import {ViewUserInstancesComponent} from "./admin/feature/userManagement/viewUserInstances/viewUserInstances.component";
import {SubsectionsListComponent} from "./admin/feature/subsections/subsections-list/subsections-list.component";
import {
  SubsectionsStudentsComponent
} from "./admin/feature/subsections/subsections-students/subsections-students.component";

const routes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'ServerError',
    component: ServerErrorComponent
  },
  {
    path: 'pageNotFound',
    component: PageNotFoundComponent
  },
  {
    path: 'login',
    component: LoginRootComponent,
    children: [
      {
        path: '',
        component: LoginFormComponent,
        resolve: {
          ready: LoginResolver,
        },
      },
      {
        path: 'terms',
        component: LoginTermsComponent,
        canActivate: [authGuard],
      },
      {
        path: 'research',
        component: LoginResearchComponent,
        canActivate: [authGuard],
      },
    ],
  },
  {
    path: 'dashboard',
    canMatch: [() => permissionsGuard('ExamAdminDash')],
    component: DashboardWrapperComponent,
    canActivate: [authGuard, requiredAgreementsGuard, optionalAgreementsGuard],
    canActivateChild: [authGuard, requiredAgreementsGuard, optionalAgreementsGuard],
    children: [
      {
        path: '',
        component: InstructorDashboardComponent,
      },
      {
        path: 'UserManagement',
        component: UserManagementMenuComponent,
      },
      {
        path: 'UserManagement/AllUsers',
        component: UserManagementListComponent
      },
      {
        path: 'UserManagement/UserRoles',
        component: UserRolesListComponent
      },
      {
        path: 'UserManagement/UserRoles/Add',
        component: UserRoleAddEditComponent,
        canDeactivate: [pendingChangesGuard]
      },
      {
        path: 'UserManagement/UserRoles/Edit',
        component: UserRoleAddEditComponent,
        canDeactivate: [pendingChangesGuard]
      },
      {
        path: 'UserManagement/AllUsers/AddNewUser',
        component: AddNewUserComponent,
        canDeactivate: [pendingChangesGuard]
      },
      {
        path: 'UserManagement/AllUsers/ImportUsers',
        component: ImportUsersComponent,
        canDeactivate: [pendingChangesGuard]
      },
      {
        path: 'UserManagement/AllUsers/ImportUsers/AutomaticImport',
        component: AutoImportUsersComponent,
        canDeactivate: [pendingChangesGuard]
      },
      {
        path: 'UserManagement/AllUsers/ImportUsers/AutomaticImportList',
        component: AutoImportUsersConfigListComponent
      },
      {
        path: 'UserManagement/AllUsers/ImportUsers/AutomaticImport/Log',
        component: AutoImportLogComponent
      },
      {
        path: 'UserManagement/UserRoles/Assignees',
        component: RoleAssigneesComponent
      },
      {
        path: 'UserManagement/instances',
        component: ViewUserInstancesComponent
      },
      {
        path: 'AgreementManagement/list',
        component: AgreementManagementList,
      },
      {
        path: 'AgreementManagement/edit',
        component: CreateEditAgreement,
      },
      {
        path: 'AgreementManagement/add',
        component: CreateEditAgreement,
      },
      {
        path: 'AgreementManagement/edit',
        component: CreateEditAgreement,
      },
      {
        path: 'AgreementManagement/responses',
        component: AgreementResponseList,
      },
      {
        path: 'profile',
        component: UserProfileComponent,
      },
      {
        path: 'AgreementManagement/preview',
        component: AgreementPreview
      },
      {
        path: 'ContentViewer/SubjectList',
        component: SubjectListComponent
      },
      {
        path: 'ContentViewer/SubjectList/CCLTree/LearningObjectives',
        component: LearningObjectiveList
      },
      {
        path: 'ContentViewer/SubjectList/CCLTree',
        component: CCLTreeComponent
      },
      {
        path: 'ContentViewer/SubjectList/CCLTree/LearningObjectives/ProblemTemplates',
        component: ProblemTemplateList
      },
      {
        path: 'ContentViewer/SubjectList/CCLTree/LearningObjectives/ProblemTemplates/Preview',
        component: ProblemTemplatePreview
      },
      {
        path: 'exam/create',
        component: CreateComponent,
        canDeactivate: [pendingChangesGuard],
      },
      {
        path: 'exam/list',
        component: ListExamComponent
      },
      {
        path: 'exam/preview',
        component: ExamTakeComponent
      },
      {
        path: 'exam/preview/take',
        component: ExamTakeComponent
      },
      {
        path: 'exam/graders',
        component: ExamGradersComponent
      },
      {
        path: 'exam/submissions',
        component: ExamSubmissionsComponent
      },
      {
        path: 'exam/submissions/review',
        component: ExamSubmissionReviewComponent
      },
      {
        path: 'exam/instances',
        component: ExamAllInstancesComponent
      },
      {
        path: 'exam/instances/comments',
        component: ExamInstanceCommentsComponent
      },
      {
        path: 'exam/export/config',
        component: ExamExportConfigComponent
      },
      {
        path: 'exam/export/list',
        component: ExamExportListComponent
      },
      {
        path: 'SystemManagement',
        component: SystemManagementMenuComponent
      },
      {
        path: 'SystemManagement/lists',
        component: SystemListHubComponent
      },
      {
        path: 'SystemManagement/organizations',
        component: OrganizationListComponent
      },
      {
        path: 'SystemManagement/organizations/config',
        component: SystemConfigComponent
      },
      {
        path: 'SystemManagement/subjects',
        component: SystemSubjectListComponent
      },
      {
        path: 'SystemManagement/subjects/config',
        component: SystemConfigComponent
      },
      {
        path: 'SystemManagement/units',
        component: UnitListComponent
      },
      {
        path: 'SystemManagement/units/config',
        component: SystemConfigComponent
      },
      {
        path: 'SystemManagement/courses',
        component: CourseListComponent
      },
      {
        path: 'SystemManagement/courses/config',
        component: SystemConfigComponent
      },
      {
        path: 'SystemManagement/branding',
        component: SystemBrandingComponent
      },
      {
        path: 'SystemManagement/Announcement/Config',
        component: AnnouncementConfigComponent
      },
      {
        path: 'SystemManagement/Announcement/List',
        component: AnnouncementListComponent
      },
      {
        path: 'SystemManagement/logs',
        component: SystemLogListComponent
      },
      {
        path: 'subsections/list',
        component: SubsectionsListComponent
      },
      {
        path:'subsections/students',
        component: SubsectionsStudentsComponent
      },
      {
        path: 'Guides',
        component: GuideListComponent
      }
    ],
  },
  {
    path: 'dashboard',
    canMatch: [() => permissionsGuard('ExamStudentDash')],
    component: DashboardWrapperComponent,
    canActivate: [authGuard, requiredAgreementsGuard, optionalAgreementsGuard],
    canActivateChild: [authGuard, requiredAgreementsGuard, optionalAgreementsGuard],
    children: [
      {
        path: '',
        component: StudentDashboardComponent,
      },
      {
        path: 'exam/take',
        component: ExamTakeComponent,
        canDeactivate: [pendingChangesGuard]
      },
      {
        // TODO need to do checks so that the student cannot view other profiles
        path: 'profile',
        component: UserProfileComponent
      },
      {
        path: 'exam/ViewGrade',
        component: ExamSubmissionReviewComponent
      }
    ]
  },
  {
    path: 'dashboard',
    canMatch: [() => permissionsGuard('ClassroomStudentDash')],
    component: DashboardWrapperComponent,
    canActivate: [authGuard, requiredAgreementsGuard, optionalAgreementsGuard],
    canActivateChild: [authGuard, requiredAgreementsGuard, optionalAgreementsGuard],
    children: [
      {
        path: '',
        component: StudentClassroomDashboardComponent,
      },
      {
        path: 'assessments',
        component: StudentClassroomAssessmentsComponent
      },
      {
        path: 'gradebook',
        component: StudentGradebookComponent
      },
      {
        path: 'assessment/take',
        component: ExamTakeComponent,
        canDeactivate: [pendingChangesGuard]
      },
      {
        // TODO need to do checks so that the student cannot view other profiles
        path: 'profile',
        component: UserProfileComponent
      },
      {
        path: 'exam/ViewGrade',
        component: ExamSubmissionReviewComponent
      }
    ]
  },
  {
    path: 'dashboard',
    canMatch: [() => permissionsGuard('FORBIDDEN')],
    component: ForbiddenErrorComponent
  },
  {
    path: '**',
    redirectTo: 'pageNotFound',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { canceledNavigationResolution: 'computed' }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
